import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faGripLines, faThumbtack } from '@fortawesome/free-solid-svg-icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { useTheme } from '../../context/ThemeContext';
import { SECTION_META, resolveOrder } from '../../config/epkSections';

function SortableSectionRow({ id, label, icon, isLight }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-rounded-lg tw-mb-1 tw-transition-colors
        ${isLight ? 'tw-bg-black/5' : 'tw-bg-white/5'}
        ${isDragging ? 'tw-opacity-60 tw-shadow-lg tw-z-10 tw-relative' : ''}
      `}
    >
      <FontAwesomeIcon icon={icon} className={`tw-w-4 tw-shrink-0 ${isLight ? 'tw-text-[#7C3AED]' : 'tw-text-[#FFB0CF]'}`} />
      <span className={`tw-flex-1 tw-text-sm tw-font-semibold tw-truncate ${isLight ? 'tw-text-[#374151]' : 'tw-text-white'}`}>
        {label}
      </span>
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={label}
        className={`tw-flex tw-items-center tw-justify-center tw-p-2 tw-touch-none tw-cursor-grab active:tw-cursor-grabbing tw-bg-transparent tw-border-none tw-shrink-0
          ${isLight ? 'tw-text-[#9CA3AF]' : 'tw-text-white/40'}
        `}
      >
        <FontAwesomeIcon icon={faGripLines} />
      </button>
    </div>
  );
}

/**
 * Compact table-of-contents reorder control, launched from EditNavBar.
 * Desktop: dropdown anchored under the Reorder button.
 * Mobile/tablet: bottom sheet.
 * Both render the same list; dnd-kit is used on every breakpoint.
 *
 * Two-stage commit: the control keeps its own scratch order while dragging.
 * "Apply" writes it into the draft via onApply(nextOrder). Closing without
 * Apply (backdrop, X, outside click) discards the scratch order - the real
 * persist/revert stays on the existing Save/Discard flow.
 */
export default function ReorderSectionsControl({ isOpen, onClose, sectionOrder, onApply, onDraggingChange }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const containerRef = useRef(null);

  const [scratchOrder, setScratchOrder] = useState(() => resolveOrder(sectionOrder));
  const [isDragging, setIsDragging] = useState(false);

  // Reset the scratch order from the current draft every time the control opens,
  // so a previous unapplied drag never leaks into a later session.
  useEffect(() => {
    if (isOpen) {
      setScratchOrder(resolveOrder(sectionOrder));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (onDraggingChange) onDraggingChange(isDragging);
  }, [isDragging, onDraggingChange]);

  // GOTCHA: a drag starts with a mousedown that can leave the control before
  // the drag is recognized. Never auto-close on outside mousedown while a
  // drag is active, or dnd-kit loses its drop target mid-gesture.
  useEffect(() => {
    if (!isOpen) return undefined;

    function handleClickOutside(event) {
      if (isDragging) return;
      const el = containerRef.current;
      // containerRef is the desktop dropdown. On mobile it's display:none
      // (offsetParent === null) and the portaled backdrop handles closing,
      // so bail here to avoid closing the sheet on every tap.
      if (!el || el.offsetParent === null) return;
      if (!el.contains(event.target)) onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isDragging, onClose]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = () => setIsDragging(true);

  const handleDragEnd = (event) => {
    setIsDragging(false);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setScratchOrder((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleDragCancel = () => setIsDragging(false);

  const handleApply = () => {
    onApply(scratchOrder);
    onClose();
  };

  if (!isOpen) return null;

  const listContent = (
    <>
      {/* Cover: pinned, non-draggable, dimmed, always first */}
      <div
        className={`tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-rounded-lg tw-mb-2 tw-opacity-50
          ${isLight ? 'tw-bg-black/5' : 'tw-bg-white/5'}
        `}
      >
        <FontAwesomeIcon icon={SECTION_META.cover.icon} className={`tw-w-4 tw-shrink-0 ${isLight ? 'tw-text-[#7C3AED]' : 'tw-text-[#FFB0CF]'}`} />
        <span className={`tw-flex-1 tw-text-sm tw-font-semibold tw-truncate ${isLight ? 'tw-text-[#374151]' : 'tw-text-white'}`}>
          {t(SECTION_META.cover.labelKey)}
        </span>
        <FontAwesomeIcon icon={faThumbtack} className={`tw-shrink-0 ${isLight ? 'tw-text-[#9CA3AF]' : 'tw-text-white/40'}`} />
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={scratchOrder} strategy={verticalListSortingStrategy}>
          {scratchOrder.map((id) => (
            <SortableSectionRow
              key={id}
              id={id}
              label={t(SECTION_META[id]?.labelKey || id)}
              icon={SECTION_META[id]?.icon || SECTION_META.cover.icon}
              isLight={isLight}
            />
          ))}
        </SortableContext>
      </DndContext>
    </>
  );

  const panelInner = (
    <>
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <h3 className={`tw-text-base tw-font-bold ${isLight ? 'tw-text-[#374151]' : 'tw-text-white'}`}>
            {t('Reorder Sections')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('Close')}
            className={`tw-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-rounded-full tw-bg-transparent tw-border-none tw-cursor-pointer
              ${isLight ? 'tw-text-[#374151] hover:tw-bg-black/5' : 'tw-text-white hover:tw-bg-white/10'}
            `}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <p className={`tw-text-xs tw-mb-3 ${isLight ? 'tw-text-[#6B7280]' : 'tw-text-[#E2BDC9]'}`}>
          {t('Drag sections to change the order they appear on your EPK.')}
          {' '}
          {t('Cover is always first')}.
        </p>

        <div>{listContent}</div>

        <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4 tw-pt-3 tw-border-t tw-border-white/10">
          <button
            type="button"
            onClick={onClose}
            className={`tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-bold tw-border tw-cursor-pointer tw-bg-transparent
              ${isLight
                ? 'tw-border-black/10 tw-text-[#374151] hover:tw-bg-black/5'
                : 'tw-border-white/20 tw-text-white hover:tw-bg-white/10'}
            `}
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="tw-px-5 tw-py-2 tw-rounded-full tw-text-sm tw-font-bold tw-tracking-widest tw-border-none tw-cursor-pointer tw-bg-[#FF43A7] tw-text-[#570033] hover:tw-bg-[#ff5cac]"
          >
            {t('Apply')}
          </button>
        </div>
    </>
  );

  return (
    <>
      {/* DESKTOP: dropdown anchored under the Reorder button. Stays inside the
          button's relative wrapper so it tracks the anchor. */}
      <div
        ref={containerRef}
        className="tw-hidden md:tw-block tw-absolute tw-top-full tw-right-0 tw-mt-2 tw-w-[300px] tw-max-h-[70vh] tw-overflow-y-auto tw-rounded-xl tw-border tw-p-4 tw-z-[1100] tw-bg-[#280D41] tw-border-[#FF43A7]/50 tw-shadow-2xl"
      >
        {panelInner}
      </div>

      {/* MOBILE: portaled to <body> so the fixed bottom sheet escapes
          EditNavBar's backdrop-filter, which otherwise establishes a containing
          block and pins the sheet to the top of the navbar instead of the viewport. */}
      {createPortal(
        <div className="md:tw-hidden">
          <div
            className="tw-fixed tw-inset-0 tw-bg-black/60 tw-z-[1090]"
            onClick={() => !isDragging && onClose()}
          />
          <div className="tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-[1100] tw-max-h-[80vh] tw-overflow-y-auto tw-rounded-t-2xl tw-border-t tw-p-4 tw-bg-[#280D41] tw-border-[#FF43A7]/50 tw-shadow-2xl">
            {panelInner}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
